import React, { useEffect, useRef, useState } from 'react';
import { apiCall, readJsonResponse } from '../utils/apiHelper';
import './EstimateTransfer.css';

const MONTH_OPTIONS = [
  '3月', '4月', '5月', '6月', '7月', '8月',
  '9月', '10月', '11月', '12月', '1月', '2月',
];

const MIN_FISCAL_YEAR = 2026;
const BUSY_TEXT = '他のプロセスで集計中のため実行できません。お待ちください。';
const DEFAULT_SUPPORT_EMAIL = '＜メルアド＞';
const MSG_MISSING_FORMAT_EXCEL =
  '販促メディア一括稟議.xlsxファイルが指定のBOXに格納されていません。';
const MSG_MISSING_PDF =
  '指定のBOXに見積書のPDFファイルが格納されていません。';
const MSG_WATCH_EXCEL_LOCKED = [
  '現在、「販促メディア一括稟議」のExcelファイルがロックされています。',
  'ロックを解除し、再度「集計する」ボタンをクリックして実行してください。',
].join('\n');

function unavailableText(email) {
  return [
    '現在利用できない状態となっております。',
    'ご迷惑をお掛けし申し訳ございません。',
    'お手数ですが、システム本部までお問い合わせください。',
    email || DEFAULT_SUPPORT_EMAIL,
  ].join('\n');
}

function countStatusText(result, useLegacy) {
  const added = result.rowCount || 0;
  const updated = result.updatedCount || 0;
  const files = result.fileCount || 0;
  const source = '見積PDF';
  if (updated > 0 || (added === 0 && updated === 0)) {
    return `${source}${files}ファイルから、${added}項目を追加し、${updated}項目を変更しました。`;
  }
  return `${source}${files}ファイルから、${added}項目を追加しました。`;
}

function extraErrorText(result) {
  if (!result) {
    return '';
  }
  const kind = result.displayKind;
  if (kind === 'missing_format_excel') {
    return MSG_MISSING_FORMAT_EXCEL;
  }
  if (kind === 'missing_pdf') {
    return MSG_MISSING_PDF;
  }
  if (kind === 'watch_excel_locked') {
    return MSG_WATCH_EXCEL_LOCKED;
  }
  if (kind === 'unavailable' || kind === 'error') {
    return unavailableText(result.supportEmail);
  }
  return '';
}

function noteItems(details) {
  if (!Array.isArray(details)) {
    return [];
  }
  return details.map((line) => {
    if (line && typeof line === 'object' && !Array.isArray(line)) {
      return {
        text: line.text != null ? String(line.text) : '',
        error: Boolean(line.error),
      };
    }
    return { text: String(line), error: true };
  }).filter((item) => item.text);
}

function noteCopyText(noteList) {
  return noteList.map((item) => item.text).join('\n');
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error('copy failed');
  }
}

function tokyoDate() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());
  const num = (type) => Number(parts.find((p) => p.type === type)?.value);
  return { year: num('year'), month: num('month'), day: num('day') };
}

function tokyoYearMonth() {
  const { year, month } = tokyoDate();
  return { year, month };
}

function addCalendarMonths(year, month, delta) {
  const index = year * 12 + (month - 1) + delta;
  const y = Math.floor(index / 12);
  const m = ((index % 12) + 12) % 12 + 1;
  return { year: y, month: m };
}

function fiscalYearOf(calendarYear, calendarMonth) {
  return calendarMonth <= 2 ? calendarYear - 1 : calendarYear;
}

function parseMonthNumber(monthLabel) {
  return Number(String(monthLabel || '').replace('月', ''));
}

function calendarYearOf(fiscalYear, monthNumber) {
  return monthNumber <= 2 ? fiscalYear + 1 : fiscalYear;
}

/**
 * 年月変更時のトグル初期値。
 * - 前月以前: ON
 * - 当月: 7日より前は OFF、7日以降は ON
 * - それ以外: OFF
 */
function defaultToggleOn(fiscalYear, monthLabel, today = tokyoDate()) {
  const monthNumber = parseMonthNumber(monthLabel);
  if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) {
    return false;
  }
  const selectedYear = calendarYearOf(fiscalYear, monthNumber);
  const prev = addCalendarMonths(today.year, today.month, -1);
  if (
    selectedYear < prev.year
    || (selectedYear === prev.year && monthNumber <= prev.month)
  ) {
    return true;
  }
  if (selectedYear === today.year && monthNumber === today.month) {
    return Number.isInteger(today.day) && today.day >= 7;
  }
  return false;
}

const SWITCH_TITLE = '実行稟議向けとして追加する';

function defaultSpecified(fiscalYearOptions) {
  const today = tokyoDate();
  const target = addCalendarMonths(today.year, today.month, 1);
  const years = fiscalYearOptions && fiscalYearOptions.length > 0
    ? fiscalYearOptions
    : availableFiscalYears();
  let fy = fiscalYearOf(target.year, target.month);
  if (!years.includes(fy)) {
    fy = years[years.length - 1] || MIN_FISCAL_YEAR;
  }
  const month = `${target.month}月`;
  return {
    fiscalYear: fy,
    month,
    useLegacy: defaultToggleOn(fy, month, today),
  };
}

function currentFiscalYear() {
  const { year, month } = tokyoYearMonth();
  if (!Number.isInteger(year) || !Number.isInteger(month)) {
    return MIN_FISCAL_YEAR;
  }
  return month <= 2 ? year - 1 : year;
}

function availableFiscalYears() {
  const today = tokyoDate();
  const ahead = addCalendarMonths(today.year, today.month, 1);
  const current = Math.max(
    currentFiscalYear(),
    fiscalYearOf(ahead.year, ahead.month),
    MIN_FISCAL_YEAR,
  );
  const years = [];
  for (let y = MIN_FISCAL_YEAR; y <= current; y += 1) {
    years.push(y);
  }
  return years;
}

function fiscalYearLabel(year) {
  return `${String(year % 100).padStart(2, '0')}年度`;
}

function EstimateTransfer() {
  const fiscalYearOptions = availableFiscalYears();
  const [fiscalYear, setFiscalYear] = useState(
    () => defaultSpecified(fiscalYearOptions).fiscalYear,
  );
  const [month, setMonth] = useState(() => defaultSpecified(fiscalYearOptions).month);
  const [useLegacy, setUseLegacy] = useState(
    () => defaultSpecified(fiscalYearOptions).useLegacy,
  );
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState('');
  const [result, setResult] = useState(null);
  const [notesCopied, setNotesCopied] = useState(false);
  const selfTransferRef = useRef(false);
  const notesCopiedTimerRef = useRef(null);
  const statusReqIdRef = useRef(0);
  /** 自集計完了直後は GCS ロック残存で running=true になり結果が消えるのを防ぐ */
  const ignoreRemoteBusyUntilRef = useRef(0);

  const applyStatus = (data, reqId) => {
    if (reqId != null && reqId !== statusReqIdRef.current) {
      return;
    }
    // 自分が実行中の間は「他プロセス」表示にしない
    if (selfTransferRef.current) {
      return;
    }
    const running = Boolean(data?.running);
    if (running && Date.now() < ignoreRemoteBusyUntilRef.current) {
      setBusy(false);
      setBusyMessage('');
      return;
    }
    setBusy(running);
    setBusyMessage(running ? (data.message || BUSY_TEXT) : '');
    // 他プロセス実行中のみ前回結果を隠す（自集計完了結果は上のガードで残す）
    if (running) {
      setResult(null);
    }
  };

  useEffect(() => {
    let stopped = false;
    let timerId = null;

    const nextIntervalMs = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return 15_000;
      }
      if (selfTransferRef.current || busy || loading) {
        return 2_000;
      }
      return 5_000;
    };

    const poll = async () => {
      const reqId = ++statusReqIdRef.current;
      try {
        const res = await apiCall('/api/transfer');
        const data = await readJsonResponse(res);
        if (stopped || !res.ok || !data) {
          return;
        }
        applyStatus(data, reqId);
      } catch {
        // ignore transient poll errors
      }
    };

    const tick = async () => {
      if (stopped) {
        return;
      }
      await poll();
      if (!stopped) {
        timerId = setTimeout(tick, nextIntervalMs());
      }
    };

    const onVisibility = () => {
      if (stopped || document.visibilityState !== 'visible') {
        return;
      }
      if (timerId != null) {
        clearTimeout(timerId);
      }
      tick();
    };

    tick();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      stopped = true;
      if (timerId != null) {
        clearTimeout(timerId);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [busy, loading]);

  useEffect(() => () => {
    if (notesCopiedTimerRef.current) {
      clearTimeout(notesCopiedTimerRef.current);
    }
  }, []);

  const handlePdfAggregate = async () => {
    await runAggregate('/api/pdf-aggregate');
  };

  const handleAggregate = async () => {
    // 常にPDF集計を実行（トグルはB列「追加」マーカーの有無のみを制御）
    await handlePdfAggregate();
  };

  const runAggregate = async (path) => {
    selfTransferRef.current = true;
    statusReqIdRef.current += 1; // 進行中のステータス応答を無効化
    setLoading(true);
    setBusy(false);
    setBusyMessage('');
    setResult(null);
    setNotesCopied(false);
    let keepBusy = false;
    try {
      const res = await apiCall(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, fiscalYear, writeAddMarker: useLegacy }),
      });
      const data = await readJsonResponse(res);
      if (!data) {
        setResult({ displayKind: 'unavailable', details: [] });
        return;
      }
      if (res.status === 409 || (data.running && data.status === 'error')) {
        keepBusy = true;
        setBusy(true);
        setBusyMessage(data.message || BUSY_TEXT);
        setResult(null);
        return;
      }
      const kind = data.displayKind
        || (res.ok && data.status === 'ok' ? 'ok' : 'unavailable');
      setResult({ ...data, displayKind: kind });
    } catch (e) {
      setResult({
        displayKind: 'unavailable',
        details: [],
      });
    } finally {
      if (!keepBusy) {
        setBusy(false);
        setBusyMessage('');
        // 自集計完了直後の GET が GCS ロック残で running=true を返すと結果が消えるため短時間無視
        ignoreRemoteBusyUntilRef.current = Date.now() + 15_000;
      } else {
        // 他プロセス 409 時は poll の running=true を無視しない（表示を維持）
        ignoreRemoteBusyUntilRef.current = 0;
      }
      setLoading(false);
      selfTransferRef.current = false;
      // 直後の GET が古い「実行中」を返すと「他プロセス」が一瞬出るため、ここでは refresh しない
    }
  };

  const disabled = loading || busy;
  const kind = result?.displayKind;
  const showCounts = Boolean(result)
    && kind !== 'missing_format_excel'
    && kind !== 'missing_pdf'
    && kind !== 'watch_excel_locked'
    && kind !== 'unavailable'
    && kind !== 'error';
  const countText = showCounts ? countStatusText(result, useLegacy) : '';
  const errorText = extraErrorText(result);
  const noteList = noteItems(result?.details);

  const handleCopyNotes = async () => {
    if (noteList.length === 0) {
      return;
    }
    try {
      await copyText(noteCopyText(noteList));
      setNotesCopied(true);
      if (notesCopiedTimerRef.current) {
        clearTimeout(notesCopiedTimerRef.current);
      }
      notesCopiedTimerRef.current = setTimeout(() => {
        setNotesCopied(false);
        notesCopiedTimerRef.current = null;
      }, 2000);
    } catch {
      setNotesCopied(false);
    }
  };

  return (
    <div className="estimate-transfer">
      <h1 className="estimate-transfer-title">広告代理店見積書集計ツール</h1>
      <p className="estimate-transfer-lead">
        「販促メディア一括稟議」のExcelファイルに集計します
      </p>
      <p className="estimate-transfer-note">
        ※指定のBOXに「見積書」のPDFファイルが格納されていることをご確認ください。
      </p>

      <div className="Transfer-controls">
        <div className="Transfer-selects">
          <label className="Month-select-label" htmlFor="fiscal-year-select">
            <select
              id="fiscal-year-select"
              className="Month-select Year-select"
              value={fiscalYear}
              onChange={(e) => {
                const next = Number(e.target.value);
                setFiscalYear(next);
                setUseLegacy(defaultToggleOn(next, month));
              }}
              disabled={disabled}
              aria-label="年度"
            >
              {fiscalYearOptions.map((y) => (
                <option key={y} value={y}>{fiscalYearLabel(y)}</option>
              ))}
            </select>
          </label>
          <label className="Month-select-label" htmlFor="month-select">
            <select
              id="month-select"
              className="Month-select"
              value={month}
              onChange={(e) => {
                const next = e.target.value;
                setMonth(next);
                setUseLegacy(defaultToggleOn(fiscalYear, next));
              }}
              disabled={disabled}
              aria-label="月"
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <span className="Transfer-switch-title" id="transfer-switch-title">
            <span className="Transfer-switch-title-sizer" aria-hidden="true">
              {SWITCH_TITLE}
            </span>
            <span className="Transfer-switch-title-text">{SWITCH_TITLE}</span>
          </span>
          <div className="Transfer-switch-block">
            <label className={`Transfer-switch${useLegacy ? ' is-on' : ''}`}>
              <input
                type="checkbox"
                role="switch"
                checked={useLegacy}
                onChange={(e) => setUseLegacy(e.target.checked)}
                disabled={disabled}
                aria-labelledby="transfer-switch-title"
              />
              <span className="Transfer-switch-track" aria-hidden="true">
                <span className="Transfer-switch-thumb" />
              </span>
            </label>
          </div>
        </div>
        <button
          className="Transfer-button"
          type="button"
          onClick={handleAggregate}
          disabled={disabled}
        >
          {loading ? '集計中…' : '集計する'}
        </button>
      </div>

      {(loading || busy || result) && (
      <div className="Transfer-output">
        <section
          className="Transfer-panel Transfer-panel-status"
          aria-label="完了・確認・エラー"
        >
          <div className="Transfer-panel-body">
            {loading && (
              <>
                <p className="Transfer-panel-empty">集計中です…</p>
                <div
                  className="Transfer-progress"
                  role="progressbar"
                  aria-busy="true"
                  aria-valuetext="集計中"
                  aria-label="集計中"
                >
                  <div className="Transfer-progress-bar" />
                </div>
              </>
            )}
            {!loading && busy && (
              <p className="Transfer-busy" role="alert">
                {busyMessage || BUSY_TEXT}
              </p>
            )}
            {!loading && !busy && countText && (
              <p className="Transfer-status-message" role="status">
                {countText}
              </p>
            )}
            {!loading && !busy && errorText && (
              <p className="Transfer-status-message is-error" role="alert">
                {errorText}
              </p>
            )}
          </div>
        </section>

        {result && noteList.length > 0 && (
        <section className="Transfer-panel Transfer-panel-notes" aria-label="メモ">
          <div className="Transfer-panel-header">
            <h2 className="Transfer-panel-title">メモ</h2>
            <button
              type="button"
              className={`Transfer-copy-button${notesCopied ? ' is-copied' : ''}`}
              onClick={handleCopyNotes}
              aria-label={notesCopied ? 'メモをコピーしました' : 'メモをコピーする'}
              title={notesCopied ? 'コピーしました' : 'メモをコピーする'}
            >
              {notesCopied ? (
                <svg className="Transfer-copy-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg className="Transfer-copy-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
              )}
            </button>
          </div>
          <div className="Transfer-panel-body">
            {noteList.length > 0 ? (
              <ul>
                {noteList.map((item, i) => (
                  <li
                    key={`${i}-${item.text}`}
                    className={item.error ? 'is-error' : undefined}
                  >
                    {item.text}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="Transfer-panel-empty">メモはありません</p>
            )}
          </div>
        </section>
        )}
      </div>
      )}
    </div>
  );
}

export default EstimateTransfer;
